import type { ReactNode } from 'react';
import { tBoards, type BoardsI18nKey } from '../../lib/boards-i18n';
import './BoardMarks.css';

/**
 * 게시 상태 사전 — 목록 · 피드 · 트리 · 글 머리가 **같은 낱말 · 같은 모양**을 쓴다
 * (2026-09-15 · 정본 제안 11 `D4-2` · 시안 v1 `.mk` · `.tagd` · `.pf`).
 *
 * ★★ 정본 `Badge` 는 둥근 알약(상태 · 개수)이고, 게시판 표식은 **제목 옆에 붙는 작은 네모**다 —
 *    한 줄에 서너 개가 나란히 서도 제목보다 커 보이지 않아야 해서 모양을 따로 둔다(시안 확정).
 *    정본에는 «제안»으로만 올렸다 — 반영은 사용자 몫이다.
 * ★ 색은 상태색 tint/ink 짝만 쓴다(F4). 샘플의 hex 는 옮기지 않았다.
 * ★ 색만으로 뜻을 만들지 않는다 — 모든 표식은 **글자**를 갖는다(정본 P5).
 *
 * 서버 값이 아직 없는 표식(`confirm-required` · `confirmed` · `readers`)도 사전에는 둔다 —
 * 서버 요청 R1 이 들어오면 화면이 새 낱말을 지어내지 않고 여기서 꺼내 쓰게.
 */
export type BoardMarkKind =
  | 'pinned'
  | 'new'
  | 'confirm-required'
  | 'confirmed'
  | 'secret'
  | 'readers'
  | 'attachments'
  | 'default-board'
  | 'feed'
  | 'comments-off';

const LABEL: Record<Exclude<BoardMarkKind, 'attachments'>, BoardsI18nKey> = {
  pinned: 'boards.wide.pinned_badge',
  new: 'boards.wide.new_badge',
  'confirm-required': 'boards.v1.mark.confirm_required',
  confirmed: 'boards.v1.mark.confirmed',
  secret: 'boards.post.locked',
  readers: 'boards.editor.readers',
  'default-board': 'boards.v1.mark.default_board',
  feed: 'boards.settings.type_feed',
  'comments-off': 'boards.v1.post.tab.comments_off',
};

export function boardMarkLabel(kind: BoardMarkKind, count = 0): string {
  return kind === 'attachments' ? tBoards('boards.post.attachments', { n: count }) : tBoards(LABEL[kind]);
}

export function BoardMark({ kind, count }: { kind: BoardMarkKind; count?: number }) {
  return <span className={`bv1-mark bv1-mark--${kind}`}>{boardMarkLabel(kind, count)}</span>;
}

/** 말머리 칸 — 없으면 흐린 「말머리 없음」(빈 칸으로 두면 «못 불러왔나»로 읽힌다). */
export function BoardPrefixPill({ label }: { label: ReactNode | null | undefined }) {
  return label
    ? <span className="bv1-prefix">{label}</span>
    : <span className="bv1-prefix bv1-prefix--none">{tBoards('boards.editor.prefix_none')}</span>;
}
